import { useEffect } from 'react';
import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';

import { adminDispensaries } from 'shared/graphql/dispensary/queries';
import { useStores } from 'src/hooks/use-stores';
import { useGetDegradedPollInterval } from 'shared/hooks/use-get-degraded-poll-interval';

import { GqlDispensaries } from 'types/graphql';

type AdminDispensariesData = {
  filteredDispensaries?: Array<GqlDispensaries | null> | null;
};

type LoadDispensaryResult = {
  dispensaryId: string | undefined;
  loading: boolean;
  networkStatus: NetworkStatus;
};

const getFirstDispensary = (data?: AdminDispensariesData): GqlDispensaries | null =>
  _.get(data, 'filteredDispensaries[0]', null);

export default function useLoadDispensary(): LoadDispensaryResult {
  const { apolloClient, UI } = useStores();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- preserve the legacy nullish store guard
  const dispensaryId = useObserver(() => UI.dispensary?.id);
  // @ts-expect-error -- preserve the legacy mismatched key; degraded-mode behavior must change in a separate fix
  const pollInterval = useGetDegradedPollInterval({ milliseconds: 60000, increasePollInterval: 10 });

  const { data, error, networkStatus, loading } = useQuery<AdminDispensariesData>(adminDispensaries, {
    client: apolloClient,
    fetchPolicy: 'network-only',
    pollInterval,
    variables: {
      dispensaryFilter: {
        cNameOrID: id,
        includePending: true,
      },
    },
  });

  useEffect(() => {
    if (error) {
      console.error('Could not locate dispensary');
      console.error(error);
      history.replace('/redirect');
    }
  }, [error, history]);

  const dispensary = getFirstDispensary(data);

  useEffect(() => {
    if (dispensary) {
      UI.setDispensary(dispensary);
    }
  }, [dispensary, UI, history]);

  useEffect(
    () => () => {
      UI.unsetDispensary();
    },
    [UI]
  );

  return { networkStatus, dispensaryId, loading };
}
